import apiClient from '@/scripts/api/apiClient';

/**
 * User API endpoints
 */

// Query key factories
export const userKeys = {
  getAllUsers: () => ['get', 'users'],
  getAllUsersWithOrganizations: () => ['get', 'users', 'organizations'],
  getUser: (userId) => ['get', 'users', userId],
  createUser: () => ['create', 'users'],
  updateUser: (userId) => ['update', 'users', userId],
  deleteUser: (userId) => ['delete', 'users', userId],
  getMyOrganizations: () => ['get', 'users', 'me', 'organizations'],
  getUserOrganizations: (userId) => ['get', 'users', userId, 'organizations'],
  getUserComments: (userId) => ['get', 'users', userId, 'comments'],
};

/**
 * Get all users
 * @returns {Promise<Array>} List of users
 */
export const getAllUsers = async () => {
  const response = await apiClient.get('/api/users');
  return response.data;
};

/**
 * Get all users with their organizations
 * @returns {Promise<Array>} List of users with organization data
 */
export const getAllUsersWithOrganizations = async () => {
  const response = await apiClient.get('/api/users/organizations');
  return response.data;
};

/**
 * Get a specific user by ID
 * @param {string} id - User ID
 * @returns {Promise<Object>} User details
 */
export const getUser = async (id) => {
  const response = await apiClient.get(`/api/users/${id}`);
  return response.data;
};

/**
 * Create a new user
 * @param {Object} userData - User data
 * @returns {Promise<Object>} Created user
 */
export const createUser = async (userData) => {
  const response = await apiClient.post('/api/users', userData);
  return response.data;
};

/**
 * Update a user
 * @param {string} id - User ID
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} Updated user
 */
export const updateUser = async (id, userData) => {
  const response = await apiClient.put(`/api/users/${id}`, userData);
  return response.data;
};

/**
 * Delete a user
 * @param {string} id - User ID
 * @returns {Promise<Object>} Response data
 */
export const deleteUser = async (id) => {
  const response = await apiClient.delete(`/api/users/${id}`);
  return response.data;
};

/**
 * Get organizations for the current user
 * @returns {Promise<Array>} List of organizations the current user belongs to
 */
export const getMyOrganizations = async () => {
  const response = await apiClient.get('/api/users/me/organizations');
  return response.data;
};

/**
 * Get organizations for a specific user
 * @param {string} id - User ID
 * @returns {Promise<Array>} List of organizations the user belongs to
 */
export const getUserOrganizations = async (id) => {
  const response = await apiClient.get(`/api/users/${id}/organizations`);
  return response.data;
};

/**
 * Get all comments by a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} List of comments
 */
export const getUserComments = async (userId) => {
  const response = await apiClient.get(`/api/users/${userId}/comments`);
  return response.data;
};